import {
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { OrderStepConfig } from '../../../models/order-step';
import { StepHostDirective } from '../../../shared/directives/step-host.directive';
import { OrderStepBaseComponent } from '../../order-step-base-component';
import { OrderState } from 'app/order/models/order-state.model';

export interface StepCloseEvent {
    state: Partial<OrderState>;
    step: string;
    isInvalid: boolean;
}

@Component({
    selector: 'app-step-toggle',
    templateUrl: './step-toggle.component.html',
    styleUrls: ['./step-toggle.component.scss'],
    standalone: false,
})
export class StepToggleComponent implements OnChanges, OnDestroy {
    @Input()
    public state: OrderState;
    @Input()
    public orderStep: OrderStepConfig | undefined;
    @Input()
    public stepCounter: number;
    @Input()
    public open: boolean;
    @Input()
    public complete: boolean;
    @Input()
    public previousStepComplete: boolean;

    @Output()
    public clicked: EventEmitter<string | undefined> = new EventEmitter<string | undefined>();
    @Output()
    public prev: EventEmitter<Partial<OrderState>> = new EventEmitter<Partial<OrderState>>();
    @Output()
    public next: EventEmitter<Partial<OrderState>> = new EventEmitter<Partial<OrderState>>();
    @Output()
    public close = new EventEmitter<StepCloseEvent>();

    public get isInvalid(): boolean {
        return this.componentRef?.instance.isInvalid;
    }

    @ViewChild(StepHostDirective, { static: true })
    private stepHost!: StepHostDirective;

    private componentRef: ComponentRef<OrderStepBaseComponent> | undefined;

    public ngOnChanges(changes: SimpleChanges): void {
        if (!this.componentRef && this.orderStep.slug === changes.state.currentValue.currentStep) {
            this.initializeStepComponent();
        }
        const componentInstantiated = !!this.componentRef;
        const openStateChanged = changes['open'];

        const wasClosed = changes['open']?.currentValue === false && changes['open'].previousValue === true;

        if (wasClosed) {
            this.close.emit({
                state: this.componentRef.instance.getData(),
                step: this.orderStep.slug,
                isInvalid: this.componentRef.instance.isInvalid,
            });
        }
        if (openStateChanged && componentInstantiated) {
            const open = changes['open'].currentValue;
            this.componentRef.instance.isOpen = open;
        }

        const stateChanged = !!changes['state']?.currentValue;
        if (stateChanged && componentInstantiated) {
            this.componentRef.instance.state = changes['state'].currentValue as OrderState;
        }
    }

    private initializeStepComponent(): void {
        this.componentRef = this.stepHost.viewContainerRef.createComponent<OrderStepBaseComponent>(
            this.orderStep.component,
        );
        this.componentRef.instance.state = this.state;
        this.componentRef.instance.isFirstStep = this.stepCounter === 1;
        this.componentRef.instance.prev.subscribe((data) => this.prev.emit(data));
        this.componentRef.instance.next.subscribe((data) => this.next.emit(data));
    }

    public ngOnDestroy(): void {
        this.componentRef = undefined;
        this.stepHost.viewContainerRef.clear();
    }

    public get canClick(): boolean {
        if (this.open && !this.complete) {
            return false;
        }
        return this.complete || this.previousStepComplete;
    }

    protected toggleClicked(): void {
        if (this.canClick) {
            this.clicked.emit(this.orderStep.slug);
        }
    }
}
